/**
 * iter340 — Export des mouvements bancaires : comptes, dates et nature du
 * rapprochement (facture de vente, facture fournisseur, dépense rapide, salaire…).
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUTS = [
  { v: "rapproches", l: "Mouvements rapprochés", d: "ceux qui sont passés en comptabilité, avec leur pièce d'origine" },
  { v: "en_attente", l: "Mouvements à traiter", d: "ceux qui n'ont pas encore été rapprochés" },
  { v: "tous", l: "Tous les mouvements", d: "rapprochés, à traiter et ignorés" },
];

export default function ExportCamtBtn({ importId = null }) {
  const [open, setOpen] = useState(false);
  const [statut, setStatut] = useState("rapproches");
  const [format, setFormat] = useState("xlsx");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);

  const telecharger = async () => {
    setBusy(true);
    try {
      const params = { statut, format };
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      if (importId) params.import_id = importId;
      const { data } = await api.get("/journal/camt/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `mouvements_bancaires_${statut}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Fichier téléchargé");
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export impossible");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="camt-export-btn">
        <Download className="w-4 h-4 mr-2" /> Exporter les mouvements
      </Button>

      <Dialog open={open} onOpenChange={(o) => !o && !busy && setOpen(false)}>
        <DialogContent className="max-w-lg" data-testid="camt-export-dialog">
          <DialogHeader>
            <DialogTitle>Exporter les mouvements bancaires</DialogTitle>
            <DialogDescription>
              Chaque ligne indique les comptes utilisés, les dates et à quoi le mouvement a été
              rapproché : facture de vente, facture fournisseur, dépense rapide, salaire ou
              écriture bancaire directe.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            {STATUTS.map((s) => (
              <button key={s.v} type="button" onClick={() => setStatut(s.v)}
                      className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${
                        statut === s.v ? "border-[#52a675] bg-[#f0f9f4]" : "border-gray-200 hover:border-gray-300"}`}
                      data-testid={`camt-export-statut-${s.v}`}>
                <div className="text-sm font-semibold text-gray-900">{s.l}</div>
                <div className="text-xs text-gray-500">{s.d}</div>
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-end gap-3 pt-1">
            <div>
              <Label className="text-xs">Du (date de valeur)</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                     className="mt-1 w-40" data-testid="camt-export-from" />
            </div>
            <div>
              <Label className="text-xs">Au</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                     className="mt-1 w-40" data-testid="camt-export-to" />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-500 uppercase">Format</span>
            {[["xlsx", "Excel (.xlsx)"], ["csv", "CSV (.csv)"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setFormat(v)}
                      className={`text-xs px-2.5 py-1 rounded-md border ${
                        format === v ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-600 border-gray-200"}`}
                      data-testid={`camt-export-format-${v}`}>
                {l}
              </button>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Annuler</Button>
            <Button onClick={telecharger} disabled={busy} className="bg-[#52a675] hover:bg-[#3d8a5c]"
                    data-testid="camt-export-confirm">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Télécharger
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
